import { useCallback, useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useToast } from "@astrum/ui";
import { api, ApiError } from "../../lib/api.js";
import { useAuth } from "../../lib/auth.js";
import type { ChannelDetail, NotificationLevel } from "./types.js";

export interface ChannelDetailState {
  detail: ChannelDetail | undefined;
  loading: boolean;
  setDetail: Dispatch<SetStateAction<ChannelDetail | undefined>>;
  /** Re-reads the channel after its membership changes. */
  reload: () => void;
  setNotificationLevel: (level: NotificationLevel) => void;
}

/** The open channel's name, topic, members and the viewer's own settings for it. */
export function useChannelDetail(channelId: string | undefined): ChannelDetailState {
  const { viewer } = useAuth();
  const { toast } = useToast();
  const [detail, setDetail] = useState<ChannelDetail | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!channelId) {
      setDetail(undefined);
      return;
    }

    let cancelled = false;
    setLoading(true);
    api
      .get<ChannelDetail>(`/channels/${channelId}`)
      .then((res) => {
        if (!cancelled) setDetail(res);
      })
      .catch((error: unknown) => {
        if (!cancelled) toast(error instanceof ApiError ? error.message : "Could not load channel.", { tone: "danger" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [channelId, viewer]);

  /**
   * Separate from the load above, which useChannelMessages mirrors for the
   * thread: adding somebody changes who is listed, not what has been said,
   * and refetching the thread would scroll the reader away from where they were.
   */
  const reload = useCallback(() => {
    if (!channelId) return;
    api
      .get<ChannelDetail>(`/channels/${channelId}`)
      .then(setDetail)
      .catch(() => undefined);
  }, [channelId]);

  /** How much this channel may interrupt the viewer. Optimistic, because the
   *  control should not appear to stick on the old value while a request flies. */
  const setNotificationLevel = useCallback(
    (level: NotificationLevel) => {
      if (!channelId) return;
      const previous = detail?.channel.notificationLevel;

      setDetail((prev) =>
        prev ? { ...prev, channel: { ...prev.channel, notificationLevel: level } } : prev,
      );

      api.patch(`/channels/${channelId}/notifications`, { level }).catch((error: unknown) => {
        setDetail((prev) =>
          prev ? { ...prev, channel: { ...prev.channel, notificationLevel: previous } } : prev,
        );
        toast(error instanceof ApiError ? error.message : "Could not change notifications.", {
          tone: "danger",
        });
      });
    },
    [channelId, detail?.channel.notificationLevel, toast],
  );

  return { detail, loading, setDetail, reload, setNotificationLevel };
}
